import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { CPTitle, SingleTextFieldSubmit, SsoSigninBanner } from "@warden-ui/common-components";
import { SignInWithApp } from "@warden-ui/signin-with-app";

import "./CardContentSignIn.scss";
import { SignInMethodConfig } from "@warden-ui/common";
import { CPLink, CPCard } from "@warden-ui/common-components";
import { Trans, useTranslation } from "react-i18next";

interface CardContentSignInProps {
  signInMethodConfig?: SignInMethodConfig[];
  enabledEmailSignin?: boolean;
  onClickEmailSignin?: (email: string) => void;
  onClickTeamDomain?: () => void;
  title?: string;
  email?: string;
  teamName?: string;
  teamDomain?: string;
  emailDomain?: string;
  teamSelect?: boolean;
  selfSignup?: boolean;
  showSSOSignIn?: boolean;
  onClickJoinTeam?: () => void;
  errorMessage?: string;
  loading?: boolean;
  skeletonLoading?: boolean;
  children?: React.ReactNode;
}

const DisplayMode = {
  all: "all",
  email_only: "email_only",
  app_only: "app_only",
} as const;

const getDisplayMode = (signInMethodConfig: SignInMethodConfig[], enabledEmailSignin: boolean) => {
  const enabledAppSignin = signInMethodConfig.length > 0;
  if (enabledAppSignin && enabledEmailSignin) {
    return DisplayMode.all;
  } else if (enabledEmailSignin) {
    return DisplayMode.email_only;
  } else if (enabledAppSignin) {
    return DisplayMode.app_only;
  }

  return DisplayMode.all;
}

export const CardContentSignIn = ({
  signInMethodConfig = [],
  enabledEmailSignin = true,
  teamDomain = "",
  teamSelect = false,
  selfSignup = false,
  emailDomain = "",
  skeletonLoading = false,
  ...props
}: CardContentSignInProps) => {
  const [ t ] = useTranslation();
  const displayMode = getDisplayMode(signInMethodConfig, enabledEmailSignin);
  const showEmailSignin = displayMode === DisplayMode.all || displayMode === DisplayMode.email_only;
  const showAppSignin = displayMode === DisplayMode.all || displayMode === DisplayMode.app_only;
  const showAppSigninOnly = displayMode === DisplayMode.app_only;
  const showDivider = displayMode === DisplayMode.all;
  const showSelfSignup = selfSignup && emailDomain.length > 0;

  if (skeletonLoading) {
    return (
      <CPCard>
        <Stack spacing={2} className="w-100" sx={{ alignItems: "center" }}>
          <Skeleton variant="rounded" width={'60%'} height={36} />      { /* title */}
          <Skeleton variant="rounded" width={'60%'} height={20} />      { /* hint */}
          <Skeleton variant="rounded" width={'100%'} height={56} />     { /* textfield */}
          <Skeleton variant="rounded" width={'100%'} height={36.5} />   { /* button */}
        </Stack>
      </CPCard>
    );
  }

  if (showAppSigninOnly) {
    return (
      <CPCard>
        <Stack spacing={2} className="w-100">
          <CPTitle
            title={props.title ?? t('signin.title')}
            boldTitle={props.teamName}
            hint={teamDomain}
          />

          <Box className="w-100">
            <SignInWithApp config={signInMethodConfig} />
          </Box>

          {teamSelect && <CPLink
            className="mt-4"
            label={t('signin.content.change_team')}
            onClick={props.onClickTeamDomain}
            />
          }
        </Stack>
      </CPCard>
    )
  }


  return (
    <CPCard>
      <Stack spacing={2} className="w-100">
        {showEmailSignin && (
          <>
            <SingleTextFieldSubmit
              title={props.title ?? t('signin.title')}
              boldTitle={props.teamName}
              label={t('labels.email')}
              textType="email"
              hint={teamDomain}
              labelButton={t('buttons.next')}
              value={props.email}
              errorMessage={props.errorMessage}
              loading={props.loading}
              onClick={props.onClickEmailSignin}
            />

            {teamSelect && (
              <CPLink
                label={t('signin.content.change_team')}
                onClick={props.onClickTeamDomain}
              />
            )}

            {showSelfSignup && (
              <Box>
                <Typography sx={{ fontSize: '14px' }}>
                  <Trans>
                    {t('signin.content.join_team_desc', { domain: emailDomain })}
                  </Trans>
                </Typography>

                <CPLink
                  prefix={t('signin.content.join_team_prefix')}
                  label={t('signin.content.join_team_link')}
                  spacing={0.5}
                  onClick={props.onClickJoinTeam}
                />
              </Box>
            )}
          </>
        )}
        {showAppSignin && (
          <>
            <Divider hidden={!showDivider} sx={{ opacity: 1.0 }} />
            <Box className="w-100">
              <SignInWithApp config={signInMethodConfig} />
            </Box>
          </>
        )}
        {props.showSSOSignIn && (
          <SsoSigninBanner onClick={props.onClickTeamDomain} />
        )}

        {props.children}
      </Stack>
    </CPCard>
  );
};
